import type { LectureQuestion, LectureAnswersState, ReviewQuestion } from '../types';
import type { DecisionStep } from 'pages/new-questions/types';

/**
 * Identidade das questões da prova.
 *
 * Desde a modularização do Estudo de Caso, a API entrega UMA ENTRADA POR LINHA do pivot
 * `aula_questao (id_aula, id_questao, question_step_id, ordem)`:
 *
 * - `question_step_id > 0` → a entrada é UMA etapa individual do Estudo de Caso:
 *   `step_multiple` vem filtrado nessa etapa e a questão carrega `question_step_id`/`step_code`.
 * - `question_step_id = 0` (vínculo legado, o caso inteiro) → a entrada não traz esses campos e
 *   `step_multiple` vem com todas as etapas do caso.
 *
 * Ou seja, o mesmo `id_questao` pode aparecer N vezes em `questions[]`, em posições não
 * adjacentes. Identificar questão por `id_questao` colide; por isso tudo aqui usa a chave
 * composta (`id_questao` + etapa de origem).
 */

/**
 * Id da etapa que ORIGINOU a entrada em `questions[]`.
 * Vínculo por etapa → o id da etapa; vínculo legado e questões tradicionais → 0.
 * A chave pode vir no nível da questão (contrato atual) ou no pivot; lemos as duas.
 */
export const getEntryStepId = (question: LectureQuestion): number =>
  question.question_step_id ?? question.pivot?.question_step_id ?? 0;

/**
 * Identidade de UMA LINHA de `questions[]`.
 * Única porque o pivot é único em `(id_aula, id_questao, question_step_id)`.
 */
export const getEntryKey = (question: LectureQuestion): string =>
  `q${question.id_questao}-e${getEntryStepId(question)}`;

/**
 * Identidade de UMA POSIÇÃO navegável (chip do cabeçalho): entrada + etapa concreta.
 * Usa o `id` real da etapa, nunca o índice no array — é o que evita que duas entradas
 * do mesmo caso, ambas com uma única etapa (índice sempre 0), gerem a mesma chave.
 */
export const buildStepKey = (question: LectureQuestion, stepId: number): string =>
  `${getEntryKey(question)}-s${stepId}`;

/** Chave de posição resolvendo os dois formatos: etapa para case_study, entrada para os demais. */
export const buildPositionKey = (question: LectureQuestion, stepIndex = 0): string => {
  if (question.question_type !== 'case_study') return getEntryKey(question);
  const steps = question.step_multiple ?? [];
  const step = steps[stepIndex] ?? steps[0];
  return step ? buildStepKey(question, step.id) : getEntryKey(question);
};

export const findEntryByKey = (
  questions: LectureQuestion[] | undefined,
  entryKey: string,
): LectureQuestion | undefined => questions?.find((q) => getEntryKey(q) === entryKey);

export const findEntryIndexByKey = (
  questions: LectureQuestion[] | undefined,
  entryKey: string,
): number => questions?.findIndex((q) => getEntryKey(q) === entryKey) ?? -1;

/** Etapas que ESTA entrada realmente cobre: 1 no vínculo por etapa, N no legado. */
export const getEntrySteps = (question: LectureQuestion): DecisionStep[] =>
  question.question_type === 'case_study' ? (question.step_multiple ?? []) : [];

/**
 * O balde `answers.caseStudy[id_questao]` é COMPARTILHADO pelas N entradas do mesmo Estudo
 * de Caso (o `stepId` interno é único, então guardar assim não perde informação). Toda
 * leitura precisa ser restrita às etapas da entrada atual.
 */
export const getEntryStepAnswers = (
  question: LectureQuestion,
  answers: LectureAnswersState,
): Record<number, number> => {
  const bucket = answers.caseStudy[question.id_questao] ?? {};
  const entryAnswers: Record<number, number> = {};

  for (const step of getEntrySteps(question)) {
    if (bucket[step.id] !== undefined) entryAnswers[step.id] = bucket[step.id];
  }

  return entryAnswers;
};

export const countEntryAnsweredSteps = (
  question: LectureQuestion,
  answers: LectureAnswersState,
): number => Object.keys(getEntryStepAnswers(question, answers)).length;

/** Linhas da revisão que pertencem a ESTA entrada — não à questão inteira. */
export const getEntryReviewMatches = (
  question: LectureQuestion,
  reviews: ReviewQuestion[] = [],
): ReviewQuestion[] => {
  if (question.question_type !== 'case_study') {
    return reviews.filter((review) => review.question_id === question.id_questao);
  }

  const stepIds = new Set(getEntrySteps(question).map((step) => step.id));

  return reviews.filter(
    (review) =>
      review.question_type === 'case_study' &&
      review.question_id === question.id_questao &&
      stepIds.has(review.question_step_id),
  );
};

/**
 * Aplica o filtro de revisão (corretas/erradas) por POSIÇÃO da prova.
 *
 * Numa entrada de etapa única, avalia exatamente a etapa dela — sem isso uma etapa correta
 * apareceria também em "erradas" por causa de uma etapa irmã do mesmo caso. No vínculo legado
 * (caso inteiro) a entrada cobre todos os steps: aparece em "corretas" se tiver ao menos um
 * step correto e em "erradas" se tiver ao menos um errado, coerente com a coloração dos chips
 * e com a contagem do resumo. Questão sem entrada na revisão (incluída na aula depois da
 * entrega) é sempre mantida.
 */
export const filterQuestionsForReview = (
  questions: LectureQuestion[],
  reviews: ReviewQuestion[],
  filter: 'all' | 'correct' | 'wrong',
): LectureQuestion[] => {
  if (filter === 'all') return questions;

  return questions.filter((question) => {
    const matches = getEntryReviewMatches(question, reviews);
    if (matches.length === 0) return true;

    return filter === 'correct'
      ? matches.some((review) => review.is_correct)
      : matches.some((review) => !review.is_correct);
  });
};
